import React, { useMemo, useState } from 'react'
import { Table, Form } from 'react-bootstrap-v5'
import { PlanningTabProps, dateLabel, downloadCsv, forecastSources, matchesSearch, quantity, usePlanningReport } from '../helpers/PlanningHelper'
import { PlanningNotice, PlanningTiles } from './PlanningParts'

const PlanningMaterialRequirement: React.FC<PlanningTabProps> = ({ active, filters }) => {
	const [onlyShort, setOnlyShort] = useState(false)
	const [search, setSearch] = useState('')

	const { loading, data, errors } = usePlanningReport(
		active,
		'materials',
		filters.idProjects
			? {
				idProjects: filters.idProjects,
				asOfDate: filters.asOfDate,
				method: filters.method,
				horizon: filters.horizon,
				safetyDays: filters.safetyDays,
				pendingDays: filters.pendingDays,
				poDays: filters.poDays,
			}
			: null
	)

	const rows = useMemo(() => {
		return (data?.materials ?? []).filter((row: any) =>
			(!onlyShort || row.shortage > 0) && matchesSearch(search, row.itemName, row.itemCode, row.categoryName)
		)
	}, [data, onlyShort, search])

	const orderOverdue = (row: any) => row.orderBy && row.orderBy <= data.asOfDate

	const exportCsv = () => {
		downloadCsv(
			`material-requirement-${data.asOfDate}.csv`,
			['Material', 'Item Code', 'Category', 'Unit', 'Required', 'Used Per 30 Days', 'Stock', 'Open PO', 'Shortage', 'Rate', 'Required Value', 'Shortage Value', 'Lead Days', 'Bought By', 'Runs Out On', 'Order By'],
			rows.map((row: any) => [
				row.itemName, row.itemCode, row.categoryName, row.unitName, row.required, row.usedPer30Days, row.stock, row.openPo, row.shortage,
				row.rate, row.requiredValue, row.shortageValue, row.leadDays, row.leadSource, row.runOutDate, row.orderBy,
			])
		)
	}

	return (
		<>
			<p className='text-muted small mb-3'>
				Materials the Production Plan needs, from the first Formulation and Packing BOM of each product (the product BOM for Manufacturing projects).
				Shortage = required − stock − open POs. "Order by" is the day stock and open POs run out, less the usual wait between purchase order and receipt.
			</p>

			<div className='row align-items-end'>
				<div className='col-md-4'>
					<Form.Group className='mb-3' controlId='materialSearch'>
						<Form.Label>Search</Form.Label>
						<Form.Control type='text' placeholder='Material, code or category' value={search} onChange={(event) => setSearch(event.target.value)} />
					</Form.Group>
				</div>
				<div className='col-md-3'>
					<Form.Check
						className='mb-4'
						type='checkbox'
						id='materialOnlyShort'
						label='Only materials that are short'
						checked={onlyShort}
						onChange={(event) => setOnlyShort(event.target.checked)}
					/>
				</div>
				<div className='col-md-5'>
					<div className='mb-3'>
						<button className='btn btn-sm btn-outline-primary p-3' onClick={exportCsv} disabled={rows.length === 0}>
							<i className='fas fa-download me-1'></i>Excel (CSV)
						</button>
					</div>
				</div>
			</div>

			<PlanningNotice loading={loading} errors={errors} hasData={!!data} />

			{data && errors.length === 0 && (
				<>
					<div className='alert alert-secondary py-3'>
						Period: {dateLabel(data.asOfDate)} to {dateLabel(data.toDate)} ({data.horizon} days), stock as of {dateLabel(data.asOfDate)}.
						Forecast from {forecastSources(data.months)}.
						Open POs from the last {data.poDays || 'all'} days are counted as coming in.
					</div>

					{data.missing.length > 0 && (
						<div className='alert alert-warning py-3'>
							<strong>{data.missing.length} planned {data.missing.length === 1 ? 'line has' : 'lines have'} no BOM, so their materials are not in this list:</strong>
							<ul className='mb-0 mt-1'>
								{data.missing.map((row: any) => (
									<li key={row.type + row.name}>
										{row.name}: {quantity(row.quantity, 2)} {row.unitName} to {row.type === 'blend' ? 'blend' : 'make'} ({row.reason})
									</li>
								))}
							</ul>
						</div>
					)}

					<PlanningTiles
						tiles={[
							{ label: 'Volume to make', value: quantity(data.planTotals.toMakeVolume) },
							{ label: 'Materials needed', value: String(data.materials.length) },
							{ label: 'Materials short', value: String(data.totals.short), hint: 'Click to show only these', active: onlyShort, onClick: () => setOnlyShort(!onlyShort) },
							{ label: 'Required value', value: quantity(data.totals.requiredValue) },
							{ label: 'Shortage value', value: quantity(data.totals.shortageValue) },
						]}
					/>

					{data.totals.withoutRate > 0 && (
						<p className='text-muted small'>{data.totals.withoutRate} materials have no average rate, so their value is left out.</p>
					)}

					<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
						<thead className='table-light'>
							<tr>
								<th>SL</th>
								<th>Material</th>
								<th>Category</th>
								<th>Unit</th>
								<th className='text-end'>Required</th>
								<th className='text-end' title='Average use per 30 days over the last 90 days'>Used / 30 Days</th>
								<th className='text-end'>Stock</th>
								<th className='text-end'>Open PO</th>
								<th className='text-end'>Shortage</th>
								<th className='text-end'>Shortage Value</th>
								<th>Usual Wait</th>
								<th>Runs Out On</th>
								<th>Order By</th>
							</tr>
						</thead>
						<tbody>
							{rows.length === 0 && (
								<tr>
									<td colSpan={13} className='text-center text-muted py-4'>
										{data.materials.length === 0 ? 'The plan needs no materials: nothing to make, or no BOM to read.' : 'No material matches these filters.'}
									</td>
								</tr>
							)}
							{rows.map((row: any, index: number) => (
								<tr key={row.idItems}>
									<td>{index + 1}</td>
									<td>{row.itemName}{row.itemCode ? <span className='text-muted'> ({row.itemCode})</span> : null}</td>
									<td>{row.categoryName}</td>
									<td>{row.unitName}</td>
									<td className='text-end'>{quantity(row.required, 2)}</td>
									<td className='text-end text-muted'>{row.usedPer30Days > 0 ? quantity(row.usedPer30Days, 2) : ''}</td>
									<td className={`text-end ${row.stock < 0 ? 'text-danger' : ''}`} title={row.stock < 0 ? 'Negative stock is counted as none' : ''}>{quantity(row.stock, 2)}</td>
									<td className='text-end'>{row.openPo > 0 ? quantity(row.openPo, 2) : ''}</td>
									<td className={`text-end ${row.shortage > 0 ? 'text-danger fw-bold' : ''}`}>{row.shortage > 0 ? quantity(row.shortage, 2) : ''}</td>
									<td className='text-end'>{row.shortage > 0 && row.shortageValue !== null ? quantity(row.shortageValue) : ''}</td>
									<td style={{ whiteSpace: 'nowrap' }}>{row.leadDays !== null ? `${row.leadDays} days (${row.leadSource})` : ''}</td>
									<td style={{ whiteSpace: 'nowrap' }}>{dateLabel(row.runOutDate)}</td>
									<td style={{ whiteSpace: 'nowrap' }} className={orderOverdue(row) ? 'text-danger fw-bold' : ''} title={orderOverdue(row) ? 'This date has passed: order now' : ''}>
										{dateLabel(row.orderBy)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			)}
		</>
	)
}

export default PlanningMaterialRequirement
